import { gql } from '@apollo/client';
import React, { useCallback } from 'react';
import { Breadcrumbs, Breadcrumb, useBreakpoint } from '@moda/om';
import { Vertical, VERTICALS, titleize } from '@moda/portal-stanchions';
import { hrefFor } from '../../routers';
import { ProductBreadcrumbsFragment } from '../../generated/types';
import { tc } from '../../lib/trackingContext';
import { getVerticalBreadcrumbUrl } from '../../lib/getVerticalBreadcrumbUrl';

import './ProductBreadcrumbs.scss';

export const PRODUCT_BREADCRUMBS_FRAGMENT = gql`
  fragment ProductBreadcrumbsFragment on Variant {
    gender
    category
    subcategory
    name
  }
`;

interface Props {
  variant: ProductBreadcrumbsFragment;
}

const section = 'shop';

export const ProductBreadcrumbs: React.FC<Props> = React.memo(({ variant }) => {
  const { gender, category: categories, subcategory: subcategories, name } = variant;
  const vertical = VERTICALS[gender as Vertical];

  // Fine jewelry and Fashion jewelry categories come in with an underscore
  const category = categories[0]?.replace(/[_\s]/g, '-').toLowerCase();
  const subcategory = subcategories[0]?.replace(/[_\s]/g, '-').toLowerCase();

  const { matches: displayProductTitle } = useBreakpoint({ gt: 'sm' });

  const urlForVertical = getVerticalBreadcrumbUrl(vertical);

  const urlForCategory = useCallback(
    (category: string) => hrefFor.ProductCategoryListingPage({ vertical, section, category }),
    [vertical]
  );

  const urlForCategoryAndSubcategory = useCallback(
    (category: string, subcategory: string) =>
      hrefFor.ProductCategoryListingPage({ vertical, section, category, subcategory }),
    [vertical]
  );

  const handleTrackBreadcrumbClicked = useCallback(
    (url: string, text: string, event: React.MouseEvent) => {
      tc.track('Breadcrumb Clicked', {
        addToTrackingEvent: {
          url,
          text: titleize(text),
          label: titleize(text),
          coordinateX: event?.pageX || 0,
          coordinateY: event?.pageY || 0
        }
      });
    },
    []
  );

  return (
    <Breadcrumbs>
      <Breadcrumb
        onClick={(event: React.MouseEvent) =>
          handleTrackBreadcrumbClicked(urlForVertical, vertical, event)
        }
        to={urlForVertical}
      >
        {titleize(vertical)}
      </Breadcrumb>

      {category && (
        <Breadcrumb
          onClick={(event: React.MouseEvent) =>
            handleTrackBreadcrumbClicked(urlForCategory(category), category, event)
          }
          to={urlForCategory(category)}
        >
          {titleize(category)}
        </Breadcrumb>
      )}

      {category && subcategory && (
        <Breadcrumb
          onClick={(event: React.MouseEvent) =>
            handleTrackBreadcrumbClicked(
              urlForCategoryAndSubcategory(category, subcategory),
              subcategory,
              event
            )
          }
          to={urlForCategoryAndSubcategory(category, subcategory)}
        >
          {titleize(subcategory)}
        </Breadcrumb>
      )}

      {displayProductTitle && (
        <Breadcrumb className="ProductBreadcrumbs__name-breadcrumb">{name}</Breadcrumb>
      )}
    </Breadcrumbs>
  );
});
